import { type CSSProperties, PureComponent } from "react";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { CostType } from "types/enum";

import { routes } from "utilities/routes";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTSelectEditable } from "commonComponents/btWrappers/BTSelectEditable/BTSelectEditable";
import { CostCodeItemTitle } from "commonComponents/entity/costCode/CostCodeSelectorWithTag/CostCodeTitle";
import { type IDetailProps } from "commonComponents/utilities/CollectionEditable/CollectionEditable";

import CostCodeDetails from "entity/costCode/CostCodeDetails/CostCodeDetails";

interface ICostCodeSelectorProps<FormValues, TData> {
    id: KeyOfOrString<FormValues>;
    costType: CostType;
    laborCodesOnly?: boolean;
    costCodes: BTSelectItem<ICostCodeSelectorExtraData & TData>[];
    selectedCostCodeID: number;
    style?: CSSProperties;
    /**
     * Default value: -1. Determine what dropdown value to set when
     * a selected cost code is deleted from the list.
     */
    fallbackCostCodeId: number;

    /**
     * index is needed when rendering the CostCodeSelector in a list.
     */
    index?: number;
    canAdd: boolean;
    canEdit: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    onChange: (field: string, values: number[]) => void;
    onBlur: (field: string, touched: boolean) => void;
    onCostCodeChange: (
        costCodes: BTSelectItem<TData>[],
        changedCostCode: BTSelectItem<TData>,
    ) => void;
    disableDelete?: boolean;
    isInEdit?: boolean;
    hasCostCodesFeature: boolean;
    flatRateCode?: number;
    className?: string;
    placeholder?: string;
    autoFocus?: boolean;
    /** Opens the dropdown on mount. Used for cell-editor flows where one click should both enter edit mode and reveal options. */
    defaultOpen?: boolean;
    /**
     * Sentinel IDs that should render as ungrouped options at the top of the dropdown
     * even when the surrounding data is grouped. Each ID must be in its own single-child
     * group from the server for the underlying BTSelect to skip the OptGroup wrapper.
     */
    ungroupedIds?: string[];
}

export interface ICostCodeSelectorExtraData {
    canEdit?: boolean;
    isBudgeted?: boolean;
}

export class CostCodeSelector<FormValues, TData = object> extends PureComponent<
    ICostCodeSelectorProps<FormValues, TData>
> {
    static defaultProps = {
        fallbackCostCodeId: -1,
    };

    private handleCostCodeChange = (
        costCodes: BTSelectItem<ICostCodeSelectorExtraData & TData>[],
        changedCostCode: BTSelectItem<ICostCodeSelectorExtraData & TData>,
    ) => {
        let changedCostCodeId: number[] = [this.props.fallbackCostCodeId];
        for (const code of costCodes) {
            if (
                code.id === changedCostCode.id ||
                (code.children && code.children.some((child) => child.id === changedCostCode.id))
            ) {
                changedCostCodeId = [Number(changedCostCode.id)];
                break;
            }
        }
        this.props.onCostCodeChange(costCodes, changedCostCode);
        this.props.onChange(String(this.props.id), changedCostCodeId);
    };

    private itemTitle = (item: BTSelectItem<ICostCodeSelectorExtraData>) => {
        return <CostCodeItemTitle item={item} />;
    };

    private renderCostCodeDetail = (
        detailProps: IDetailProps<BTSelectItem<ICostCodeSelectorExtraData & TData>>,
    ) => {
        const matchParams: any = detailProps.routeProps.match.params;
        const costCodeId = parseInt(matchParams.costCodeId);

        let costType = CostType[matchParams.costType as keyof typeof CostType];

        // As shown in getPath(), if index is defined, then we explicitly set Cost Type and Index
        // in the route. That means we can't pull CostType from route params, so we get it from
        // props instead. This prevents the CC cell and Variance cell from matching both routes.
        if (this.props.index !== undefined) {
            costType = this.props.costType;
        }

        const modalConfig = {
            parentRoute: detailProps.parentRouteProps.match.url,
            beforeClose: () => {},
        };

        return (
            <CostCodeDetails
                costCodeId={costCodeId}
                costType={costType}
                modalConfig={modalConfig}
                index={this.props.index}
                laborCodesOnly={this.props.laborCodesOnly}
                beforeClose={() => detailProps.navigateBack()}
                onAdd={detailProps.add}
                onEdit={detailProps.edit}
                onDelete={(
                    deletedCode: BTSelectItem<ICostCodeSelectorExtraData & TData> | null,
                ) => {
                    if (deletedCode) {
                        detailProps.delete(deletedCode);
                    }
                }}
                {...detailProps.routeProps}
                disableDelete={this.props.disableDelete}
                fromCostCodeSelector
            />
        );
    };

    render() {
        const {
            costCodes: costCodeData,
            selectedCostCodeID,
            onChange,
            onBlur,
            isInEdit,
            canAdd,
            disabled,
            id,
            readOnly,
            hasCostCodesFeature,
            flatRateCode,
            placeholder,
            autoFocus,
            defaultOpen,
            ungroupedIds,
        } = this.props;

        return (
            <BTSelectEditable<FormValues, ICostCodeSelectorExtraData & TData>
                id={id}
                data-testid={`costCodeSelector_${id.toString()}`}
                renderDetail={this.renderCostCodeDetail}
                value={selectedCostCodeID}
                treeData={costCodeData}
                detailPath={this.getPath()}
                addLink={{
                    costCodeId: 0,
                    costType: CostType[this.props.costType],
                }}
                editLink={(selectedItem: BTSelectItem<ICostCodeSelectorExtraData>) => ({
                    costCodeId: selectedItem.value,
                    costType: CostType[this.props.costType],
                })}
                canAdd={canAdd && hasCostCodesFeature}
                canEdit={this.getCanEditCostCodes()}
                noTreeDataPlaceholder="No Cost Codes Found"
                notVisiblePlaceholder={placeholder}
                disabled={disabled}
                onChange={onChange}
                onBlur={onBlur}
                onTreeDataChange={this.handleCostCodeChange}
                multiple={false}
                style={{
                    ...this.props.style,
                }}
                readOnly={readOnly}
                defaultValue={flatRateCode}
                customItemRender={isInEdit ? this.itemTitle : (item) => item.title}
                className={this.props.className}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- opt-in via prop; callers decide when appropriate
                autoFocus={autoFocus}
                defaultOpen={defaultOpen}
                ungroupedIds={ungroupedIds}
            />
        );
    }

    private getPath = () => {
        if (this.props.index !== undefined) {
            return routes.costCode.getCostCodeDetailsIndexRoute(
                this.props.costType,
                this.props.index,
            );
        }
        return routes.costCode.costCodeDetails;
    };

    private getCanEditCostCodes = () => {
        const { selectedCostCodeID, costCodes, canEdit, hasCostCodesFeature } = this.props;
        // Flatten grouped structure: collect all actual cost code items (not just group parents)
        const allCostCodes =
            costCodes?.flatMap((code) =>
                code.children && code.children.length > 0 ? code.children : [code],
            ) || [];
        const selectedCostCode = allCostCodes.find((cc) => parseInt(cc.id) === selectedCostCodeID);
        if (
            selectedCostCode &&
            selectedCostCode.extraData &&
            selectedCostCode.extraData.canEdit !== undefined
        ) {
            return canEdit && selectedCostCode.extraData.canEdit && hasCostCodesFeature;
        }
        return canEdit && hasCostCodesFeature;
    };
}
